import { useState, useContext, useEffect, useCallback } from "react";
import { CancelIcon, HelpIcon, ICON_SIZE, PasskeyIcon, SignOutIcon } from "./actionIcons";
import ReactDOM from "react-dom";
import { GlobalContext } from "./GlobalContext";
import ModalTitleBar from "./ModalTitleBar";
import { apiFetch } from "./api.js";
import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startRegistration,
} from "@simplewebauthn/browser";
import { Fingerprint, Monitor, Trash2, HelpCircle, KeyRound } from "lucide-react";
import { describeDevice, formatWhen } from "./deviceLabel";
import JoyridePortal from "./JoyridePortal";
import CustomTooltip from "./JoyrideCustomTooltip";
import useSecurityTour from "./useSecurityTour";
import { themeAccent } from "./theme";

/**
 * "Security & passkeys" — the user's own credentials and their own signed-in
 * devices.
 *
 * Opened from the header profile menu rather than Settings on purpose:
 * Settings > Accounts sits behind the users.manage permission, so a
 * salesperson could never reach it, and these are exactly the things every
 * user must be able to manage for themselves.
 *
 * Wider than ChangePassword's 400px because it carries two lists. That 400 is
 * that modal's own choice, not a house rule.
 */

function SecurityPasskeysModal({ isOpen, onClose }) {
  const { getMsgBoxResult, userData } = useContext(GlobalContext);
  const [passkeys, setPasskeys] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [canEnrol, setCanEnrol] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [pkRes, sessRes] = await Promise.all([
        apiFetch("/passkeys/list", { body: {} }),
        apiFetch("/passkeys/sessions/list", { body: {} }),
      ]);
      const pk = await pkRes.json();
      const sess = await sessRes.json();
      if (pk.ok) {
        setPasskeys(pk.passkeys || []);
        setEnabled(!!pk.enabled);
      }
      if (sess.ok) setSessions(sess.sessions || []);
    } catch {
      setError("Could not load your security settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setNotice("");
    load();
    //Whether this particular device has a fingerprint reader / Face ID / Hello.
    //A device without one can still hold a passkey on a security key, so this
    //only softens the copy — it never hides the button.
    if (browserSupportsWebAuthn()) {
      platformAuthenticatorIsAvailable().then(setCanEnrol).catch(() => setCanEnrol(false));
    }
  }, [isOpen, load]);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape" && isOpen && !busy) onClose(); };
    if (isOpen) {
      document.addEventListener("keydown", onEsc);
      return () => document.removeEventListener("keydown", onEsc);
    }
  }, [isOpen, busy, onClose]);

  async function handleAddPasskey() {
    setError("");
    setNotice("");
    setBusy(true);
    try {
      const optRes = await apiFetch("/passkeys/register/options", { body: {} });
      const optObj = await optRes.json();
      if (!optRes.ok || !optObj.ok) {
        setError(optObj?.message || "Could not start setup.");
        return;
      }

      const attestation = await startRegistration({ optionsJSON: optObj.options });

      const verRes = await apiFetch("/passkeys/register/verify", {
        body: { response: attestation, label: describeDevice(navigator.userAgent) },
      });
      const verObj = await verRes.json();
      if (!verRes.ok || !verObj.ok) {
        setError(verObj?.message || "Could not finish setup.");
        return;
      }
      setPasskeys(verObj.passkeys || []);
      setNotice("This device is set up. Next time, sign in with a single tap.");
    } catch (err) {
      //A deliberate cancel and a timeout are indistinguishable by design, so
      //treat the pair as "no thanks" rather than reporting a failure.
      if (err?.name === "NotAllowedError" || err?.name === "AbortError") return;
      setError("Could not set up this device.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRemovePasskey(pk) {
    const clicked = await getMsgBoxResult({
      on: true,
      title: "Remove this passkey?",
      //The keychain note matters: without it people remove a passkey here,
      //still see it listed in iCloud/Google Password Manager, and reasonably
      //conclude the removal did not work.
      message:
        `"${pk.label || "This device"}" will no longer sign you in. ` +
        "You can still sign in with your password, and you can set this device up again later. " +
        "Your device may keep showing the passkey in its own password manager until you delete it there too.",
      buttons: ["Cancel", "Remove"],
    });
    if (clicked !== "Remove") return;

    setBusy(true);
    setError("");
    try {
      const res = await apiFetch("/passkeys/delete", { body: { passkeyID: pk.id } });
      const obj = await res.json();
      if (!res.ok || !obj.ok) { setError(obj?.message || "Could not remove it."); return; }
      setPasskeys(obj.passkeys || []);
      setNotice("Passkey removed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke(session) {
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch("/passkeys/sessions/revoke", { body: { sessionId: session.id } });
      const obj = await res.json();
      if (!res.ok || !obj.ok) { setError(obj?.message || "Could not sign that device out."); return; }
      setSessions((prev) => prev.filter((s) => s.id !== session.id));
      setNotice("That device has been signed out.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRevokeOthers() {
    const clicked = await getMsgBoxResult({
      on: true,
      title: "Sign out everywhere else?",
      message: "Every other device will need to sign in again. This device stays signed in.",
      buttons: ["Cancel", "Sign out"],
    });
    if (clicked !== "Sign out") return;

    setBusy(true);
    setError("");
    try {
      const res = await apiFetch("/passkeys/sessions/revoke-others", { body: {} });
      const obj = await res.json();
      if (!res.ok || !obj.ok) { setError(obj?.message || "Could not sign the others out."); return; }
      setSessions((prev) => prev.filter((s) => s.current));
      setNotice(obj.revoked === 1 ? "1 device signed out." : `${obj.revoked} devices signed out.`);
    } finally {
      setBusy(false);
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !busy) onClose();
  };

  //Started from in here rather than the header's help menu: this modal's scrim
  //covers the whole viewport, header included.
  const { runTour, stepIndex, steps, handleStartTutorial, handleJoyrideCallback } = useSecurityTour();

  if (!isOpen) return null;

  const others = sessions.filter((s) => !s.current);

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        className="modal-content"
        style={{
          background: 'var(--theme-surface)',
          borderRadius: 'var(--theme-radius-lg)',
          overflow: 'hidden',
          width: '560px',
          maxWidth: '90vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--theme-shadow-dialog)',
        }}
      >
        <JoyridePortal
          steps={steps}
          stepIndex={stepIndex}
          run={runTour}
          callback={handleJoyrideCallback}
          continuous
          showProgress
          showSkipButton
          disableCloseOnEsc
          disableOverlayClose
          spotlightPadding={3}
          locale={{ back: 'Back', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip' }}
          styles={{ options: { primaryColor: themeAccent(), zIndex: 10000 } }}
          tooltipComponent={CustomTooltip}
        />
        <ModalTitleBar icon={KeyRound} title="Security & passkeys" onClose={onClose} disabled={busy} />

        <div className="modal-body-padded" style={{ overflowY: 'auto' }}>
          {error && <p className="error-t" style={{ marginBottom: '12px' }}>{error}</p>}
          {notice && (
            <p style={{ color: 'var(--theme-success)', fontSize: '14px', marginBottom: '12px' }}>
              {notice}
            </p>
          )}

          {/* ------------------------------------------------- passkeys */}
          <div id="passkeys-section">
          <div className="section-label-t">Passkeys</div>
          <p className="hint-t" style={{ marginBottom: '12px' }}>
            Sign in with your fingerprint, face or screen lock instead of typing a password.
            Your password keeps working either way.
          </p>

          {loading ? (
            <p className="hint-t">Loading…</p>
          ) : passkeys.length === 0 ? (
            <p className="hint-t" style={{ marginBottom: '12px' }}>
              No passkeys yet.
            </p>
          ) : (
            <div id="passkeys-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
              {passkeys.map((pk) => (
                <div
                  key={pk.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 12px',
                    border: '1px solid var(--theme-line)',
                    borderRadius: 'var(--theme-radius-sm)',
                  }}
                >
                  <Fingerprint className="size-4" style={{ flexShrink: 0 }} aria-hidden="true" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px' }}>{pk.label || 'Passkey'}</div>
                    <div className="hint-t">
                      Added {new Date(pk.createdAt).toLocaleDateString()}
                      {/* Whether it is synced is the difference between losing a
                          phone being an inconvenience and being a lockout. */}
                      {pk.synced ? ' · synced across your devices' : ' · this device only'}
                      {pk.lastUsedAt ? ` · last used ${formatWhen(new Date(pk.lastUsedAt).getTime())}` : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-t btn-t-danger"
                    onClick={() => handleRemovePasskey(pk)}
                    disabled={busy}
                    aria-label={`Remove ${pk.label || 'passkey'}`}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {enabled ? (
            <button
              id="add-passkey-button"
              type="button"
              className="btn-t btn-t-primary"
              onClick={handleAddPasskey}
              disabled={busy}
            ><PasskeyIcon size={ICON_SIZE} />
              {busy ? 'Waiting for your device…' : 'Set up this device'}
            </button>
          ) : (
            <p className="hint-t">Passkeys are not available on this server.</p>
          )}
          {enabled && !canEnrol && (
            <p className="hint-t" style={{ marginTop: '8px' }}>
              This device has no fingerprint or face unlock. You can still use a security key.
            </p>
          )}
          </div>

          {/* ------------------------------------------ signed-in devices */}
          <div id="sessions-section">
          <div className="section-label-t" style={{ marginTop: '24px' }}>Signed-in devices</div>
          <p className="hint-t" style={{ marginBottom: '12px' }}>
            Sign out anything you do not recognise, or anything you have lost.
          </p>

          {loading ? (
            <p className="hint-t">Loading…</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {sessions.map((s) => (
                <div
                  key={s.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 12px',
                    border: '1px solid var(--theme-line)',
                    borderRadius: 'var(--theme-radius-sm)',
                  }}
                >
                  <Monitor className="size-4" style={{ flexShrink: 0 }} aria-hidden="true" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px' }}>
                      {describeDevice(s.userAgent)}
                      {s.current && <span className="pill-t" style={{ marginLeft: '8px' }}>This device</span>}
                    </div>
                    <div className="hint-t">
                      Signed in {formatWhen(s.loginAt) || 'recently'}
                      {s.loginMethod === 'passkey' ? ' with a passkey' : ''}
                    </div>
                  </div>
                  {!s.current && (
                    <button
                      type="button"
                      className="btn-t btn-t-danger"
                      onClick={() => handleRevoke(s)}
                      disabled={busy}
                    ><SignOutIcon size={ICON_SIZE} />
                      Sign out
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {others.length > 0 && (
            <button
              id="revoke-others-button"
              type="button"
              className="btn-t btn-t-secondary"
              style={{ marginTop: '12px' }}
              onClick={handleRevokeOthers}
              disabled={busy}
            ><SignOutIcon size={ICON_SIZE} />
              Sign out everywhere else
            </button>
          )}
          </div>
        </div>

        <div className="modal-shell-footer">
          <button type="button" id="security-tour-button" className="btn-t btn-t-ghost"
            onClick={handleStartTutorial} disabled={busy} title="Tour this screen">
            <HelpIcon size={ICON_SIZE} aria-hidden="true" /> Tour
          </button>
          <button type="button" className="btn-t btn-t-secondary" onClick={onClose} disabled={busy}><CancelIcon size={ICON_SIZE} />
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default SecurityPasskeysModal;
